import {
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type {
  ChatSettings,
  DebateTranscriptEntry,
  DebateTurnRequest,
  ResponseLength,
} from '@arena/shared';
import { SceneBack } from '@/shared/components/SceneBack';
import { findCharacter, portraitFor } from '../data/characters';
import { streamDebateTurn } from '../lib/debateStream';
import './WatchDebate.css';

/**
 * Live debate viewer — /arena/watch/debate.
 *
 * The user is the moderator. They arrive with a chosen pair (in router state,
 * sessionStorage fallback) and steer the exchange by typing questions in the
 * bottom input bar. After each user prompt, both characters respond in turn:
 * the side that goes first alternates so neither always opens. Each character
 * reply is intentionally short — the API caps tokens for the `dialogue` phase.
 *
 * Reveal pacing: text is revealed at a deliberate "speech" pace — slower than
 * a typewriter, with real pauses on sentence-ending and clause-ending
 * punctuation, plus a small beat between speakers. The effect is that turns
 * land like spoken statements instead of dumping on screen.
 *
 * The user can interrupt at any time — typing a new prompt and pressing Enter
 * cancels the in-flight turn and starts a new round. Whatever the character
 * actually said up to that moment is committed to the transcript with a
 * trailing em-dash so the conversation reads honestly.
 */

interface DebateLocationState {
  leftId?: string;
  rightId?: string;
  /** Display name for a user-worldview debater on the left. */
  leftName?: string;
  /** Display name for a user-worldview debater on the right. */
  rightName?: string;
  /** Worldview markdown for a user-authored left debater. */
  leftWorldview?: string;
  /** Worldview markdown for a user-authored right debater. */
  rightWorldview?: string;
  /** Craft-area settings for a user-authored left debater. */
  leftSettings?: ChatSettings;
  /** Craft-area settings for a user-authored right debater. */
  rightSettings?: ChatSettings;
}

/** A debater entry that works for both historical characters and user worldviews. */
interface Debater {
  id: string;
  name: string;
  era: string;
  worldview?: string;
  /** Worldview's own length+vocab settings; overrides the debate-wide toggle for this speaker. */
  settings?: ChatSettings;
}

interface StreamingState {
  speakerId: string;
  /** Full text streamed from the LLM so far (final once LLM is done). */
  fullText: string;
  /**
   * Number of characters of `fullText` currently visible in the bubble.
   * Driven by the speech-paced typewriter ticker. Monotonic.
   */
  reveal: number;
}

const SESSION_KEY = 'arena.watch.lastDebate';
const LENGTH_PREF_KEY = 'arena.watch.responseLength';

const LENGTH_OPTIONS: ResponseLength[] = ['short', 'medium', 'long'];

function readLengthPref(): ResponseLength {
  try {
    const raw = localStorage.getItem(LENGTH_PREF_KEY);
    if (raw === 'short' || raw === 'medium' || raw === 'long') return raw;
  } catch {
    // ignore
  }
  return 'medium';
}

function writeLengthPref(v: ResponseLength) {
  try {
    localStorage.setItem(LENGTH_PREF_KEY, v);
  } catch {
    // ignore
  }
}

/**
 * Speech-paced reveal. Base rate is roughly natural reading-aloud cadence;
 * the punctuation pauses are what sell the "speaking" effect — a real beat
 * after sentences, a softer one at clauses. Without these the text feels
 * mechanical even at a low base rate.
 */
const REVEAL_BASE_MS = 55; // ~18 cps base
const REVEAL_PAUSE_SENTENCE_MS = 380; // after . ? !
const REVEAL_PAUSE_CLAUSE_MS = 160; // after , ; :

export function WatchDebate() {
  const location = useLocation();

  // Pull the picked pair out of router state, with a sessionStorage fallback
  // so a hard-refresh on this route doesn't immediately bounce.
  const initial = useMemo<DebateLocationState>(() => {
    const fromState = (location.state ?? {}) as DebateLocationState;
    if (fromState.leftId && fromState.rightId) {
      try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(fromState));
      } catch {
        // ignore quota / privacy-mode errors
      }
      return fromState;
    }
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (raw) return JSON.parse(raw) as DebateLocationState;
    } catch {
      // ignore
    }
    return {};
  }, [location.state]);

  const leftId = initial.leftId;
  const rightId = initial.rightId;

  const left = useMemo<Debater | null>(() => {
    if (!leftId) return null;
    const char = findCharacter(leftId);
    if (char) return char;
    if (initial.leftName) return { id: leftId, name: initial.leftName, era: 'Your worldview', worldview: initial.leftWorldview, settings: initial.leftSettings };
    return null;
  }, [leftId, initial]);

  const right = useMemo<Debater | null>(() => {
    if (!rightId) return null;
    const char = findCharacter(rightId);
    if (char) return char;
    if (initial.rightName) return { id: rightId, name: initial.rightName, era: 'Your worldview', worldview: initial.rightWorldview, settings: initial.rightSettings };
    return null;
  }, [rightId, initial]);

  const [transcript, setTranscript] = useState<DebateTranscriptEntry[]>([]);
  const [streaming, setStreaming] = useState<StreamingState | null>(null);
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [responseLength, setResponseLength] = useState<ResponseLength>(() => readLengthPref());
  /** True while we're paused between turns, waiting for the user to press space. */
  const [awaitingContinue, setAwaitingContinue] = useState(false);

  // Refs mirror state so the async runTurn / interrupt closures can read the
  // latest values without re-creating themselves.
  const responseLengthRef = useRef(responseLength);
  const streamingRef = useRef<StreamingState | null>(null);
  const transcriptRef = useRef<DebateTranscriptEntry[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  const transcriptScrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Stick-to-bottom: only auto-scroll on streaming updates when the user is
  // already near the bottom. If they've scrolled up to re-read something,
  // leave them alone — re-pin once they scroll back within the threshold.
  const stickToBottomRef = useRef(true);

  useEffect(() => {
    responseLengthRef.current = responseLength;
    writeLengthPref(responseLength);
  }, [responseLength]);

  useEffect(() => {
    streamingRef.current = streaming;
  }, [streaming]);

  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  useEffect(() => {
    document.title = left && right
      ? `${left.name} vs ${right.name} — The Arena`
      : 'The Arena';
  }, [left, right]);

  // Track whether the user is pinned to the bottom. Re-pin when they scroll
  // back into the threshold; unpin the moment they scroll away.
  useEffect(() => {
    const el = transcriptScrollRef.current;
    if (!el) return;
    const PIN_THRESHOLD_PX = 80;
    const update = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      stickToBottomRef.current = distanceFromBottom <= PIN_THRESHOLD_PX;
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    return () => el.removeEventListener('scroll', update);
  }, []);

  // Auto-scroll the transcript as new content arrives, but only when the user
  // is following along at the bottom.
  useEffect(() => {
    const el = transcriptScrollRef.current;
    if (!el) return;
    if (!stickToBottomRef.current) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [transcript.length, streaming?.fullText, streaming?.reveal]);

  // Cancel any in-flight stream on unmount.
  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  // Poll-based wait for the typewriter to catch up. Defers the next runTurn()
  // so the next speaker doesn't talk over still-unread text.
  const waitForReveal = useCallback(
    (speakerId: string, target: number, signal: AbortSignal) =>
      new Promise<void>((resolve) => {
        const check = () => {
          if (signal.aborted) return resolve();
          const s = streamingRef.current;
          if (!s || s.speakerId !== speakerId) return resolve();
          if (s.reveal >= target) return resolve();
          setTimeout(check, 60);
        };
        check();
      }),
    [],
  );

  /**
   * Stream one character turn from the API; the typewriter useEffect drives
   * the reveal in parallel. We resolve when the reveal has caught up to the
   * final text, so the caller can sequence the next speaker after a beat.
   */
  const runTurn = useCallback(
    async (
      speakerId: string,
      currentTranscript: DebateTranscriptEntry[],
    ): Promise<DebateTranscriptEntry | null> => {
      if (!left || !right) return null;

      const abort = new AbortController();
      abortRef.current = abort;

      setStreaming({
        speakerId,
        fullText: '',
        reveal: 0,
      });

      const body: DebateTurnRequest = {
        speakerId,
        topic: '',
        participants: [
          { id: left.id, name: left.name, worldview: left.worldview, settings: left.settings },
          { id: right.id, name: right.name, worldview: right.worldview, settings: right.settings },
        ],
        phase: 'dialogue',
        transcript: currentTranscript,
        responseLength: responseLengthRef.current,
      };

      let accumulated = '';
      try {
        await streamDebateTurn({
          body,
          signal: abort.signal,
          onDelta: (text) => {
            accumulated += text;
            setStreaming((s) =>
              s && s.speakerId === speakerId
                ? { ...s, fullText: accumulated }
                : s,
            );
          },
        });
      } catch (err) {
        if (abort.signal.aborted) return null; // interrupt handler owns cleanup
        const message = err instanceof Error ? err.message : 'Request failed';
        setError(message);
        setStreaming((s) => (s?.speakerId === speakerId ? null : s));
        return null;
      }

      if (abort.signal.aborted) return null;

      const finalText = accumulated.trim();

      // Wait for the typewriter to finish revealing so the next speaker
      // doesn't talk over still-unread text.
      if (finalText) {
        await waitForReveal(speakerId, finalText.length, abort.signal);
        if (abort.signal.aborted) return null;
      }

      const committed: DebateTranscriptEntry = {
        speakerId,
        phase: 'dialogue',
        content: finalText,
      };
      setTranscript((prev) => [...prev, committed]);
      setStreaming((s) => (s?.speakerId === speakerId ? null : s));
      return committed;
    },
    [left, right, waitForReveal],
  );

  // Speech-paced typewriter. Advances `reveal` one char at a time, with real
  // pauses after sentence-ending and clause-ending punctuation. Driven by a
  // self-rescheduling setTimeout instead of rAF because the cadence is
  // intentionally irregular (rAF would tick 60×/sec; we want to pace by
  // content, not frames).
  useEffect(() => {
    if (!streaming) return;
    let cancelled = false;
    let timeoutId: number | undefined;

    const step = () => {
      if (cancelled) return;
      const s = streamingRef.current;
      if (!s) return;

      if (s.reveal >= s.fullText.length) {
        // Caught up to current text. The LLM may still be streaming more in;
        // poll back in 50ms to see if fullText has grown.
        timeoutId = window.setTimeout(step, 50);
        return;
      }

      const justRevealed = s.fullText[s.reveal];
      setStreaming((cur) =>
        cur ? { ...cur, reveal: cur.reveal + 1 } : cur,
      );

      let delay = REVEAL_BASE_MS;
      if (justRevealed && /[.?!…]/.test(justRevealed)) {
        delay += REVEAL_PAUSE_SENTENCE_MS;
      } else if (justRevealed && /[,;:—]/.test(justRevealed)) {
        delay += REVEAL_PAUSE_CLAUSE_MS;
      }

      timeoutId = window.setTimeout(step, delay);
    };

    timeoutId = window.setTimeout(step, REVEAL_BASE_MS);
    return () => {
      cancelled = true;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
    // Reset the loop when the speaker changes. fullText growth is read from
    // the ref inside `step`, so we don't need to re-create on every delta.
  }, [streaming?.speakerId]);

  if (!leftId || !rightId || !left || !right) {
    return <Navigate to="/arena/watch" replace />;
  }

  /**
   * Tear down the in-flight turn (if any) and commit whatever the active
   * speaker had actually said up to this point. Returns the transcript array
   * including the partial, so the caller can pass it to the next runTurn
   * without waiting on React.
   */
  const interruptAndCommit = (): DebateTranscriptEntry[] => {
    const current = streamingRef.current;
    const base = transcriptRef.current;
    if (!current) return base;

    abortRef.current?.abort();

    const heard = current.fullText.slice(0, current.reveal).trim();

    setStreaming(null);

    if (!heard) {
      return base;
    }

    const partial: DebateTranscriptEntry = {
      speakerId: current.speakerId,
      phase: 'dialogue',
      // Trailing em-dash signals the line was cut off by the moderator.
      content: heard.replace(/[.?!,—\s]+$/u, '') + ' —',
    };
    const next = [...base, partial];
    setTranscript(next);
    transcriptRef.current = next;
    return next;
  };

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!input.trim()) return;
    setError(null);

    const userText = input.trim();
    setInput('');

    // Blur the textarea on submit so the user can immediately press space
    // to advance turns without first clicking out of the input. They can
    // click back in any time to type a new prompt.
    inputRef.current?.blur();

    // INTERRUPT in-flight turn (if any) before starting a new round.
    const baseAfterInterrupt = interruptAndCommit();

    if (streamingRef.current === null && abortRef.current?.signal.aborted) {
      // Yield so the prior runTurn's catch-block can see the abort and exit.
      await new Promise((r) => setTimeout(r, 30));
    }

    const withUser: DebateTranscriptEntry[] = [
      ...baseAfterInterrupt,
      { speakerId: 'user', phase: 'dialogue', content: userText },
    ];
    setTranscript(withUser);
    transcriptRef.current = withUser;

    // New round → re-pin to bottom so the user sees the new content,
    // even if they had scrolled up to re-read earlier turns.
    stickToBottomRef.current = true;

    // Alternate who answers first each round so neither side always opens.
    const userTurnIndex = withUser.filter((t) => t.speakerId === 'user').length;
    const firstSpeaker = userTurnIndex % 2 === 1 ? left.id : right.id;
    const otherSpeaker = firstSpeaker === left.id ? right.id : left.id;

    // Infinite alternating loop. The FIRST turn after a submit auto-plays
    // (no pre-gate); every turn after that waits for the user to press
    // space. The user can interrupt by typing a new prompt; that aborts
    // the in-flight wait and starts a new round.
    let working = withUser;
    let turnIndex = 0;
    while (true) {
      const speaker = turnIndex % 2 === 0 ? firstSpeaker : otherSpeaker;
      const committed = await runTurn(speaker, working);
      if (!committed) return; // interrupted / errored
      working = [...working, committed];
      turnIndex++;

      setAwaitingContinue(true);
      const aborted = await waitForSpace(abortRef.current?.signal);
      setAwaitingContinue(false);
      if (aborted) return;
    }
    // The composer is NOT re-focused at the end — leaving focus off lets
    // the user keep pressing space without clicking out of the textarea.
  };

  const onInputKeyDown = (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  const sideFor = (id: string): 'left' | 'right' | 'user' => {
    if (id === 'user') return 'user';
    if (id === left.id) return 'left';
    return 'right';
  };

  const nameFor = (id: string): string => {
    if (id === 'user') return 'You';
    if (id === left.id) return left.name;
    if (id === right.id) return right.name;
    return 'Speaker';
  };

  const activeSpeakerId = streaming?.speakerId ?? null;

  /** What to display inside the currently-streaming bubble. */
  const visibleStreamingText = streaming
    ? streaming.fullText.slice(0, streaming.reveal)
    : '';

  return (
    <main className="scene scene--debate">
      <div
        className="scene__image scene__image--debate-stage"
        role="img"
        aria-label="A sunlit colosseum with two debate podiums facing one another"
      />
      <div className="scene__vignette" />
      <SceneBack />

      <div className="stage__controls">
        <div
          className="stage__length-toggle"
          role="group"
          aria-label="Reply length"
        >
          {LENGTH_OPTIONS.map((opt) => (
            <button
              key={opt}
              type="button"
              className={`stage__length-btn${responseLength === opt ? ' is-on' : ''}`}
              onClick={() => setResponseLength(opt)}
              aria-pressed={responseLength === opt}
            >
              {opt[0]!.toUpperCase() + opt.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <section className="stage">
        <aside
          className={`stage__figure stage__figure--left${
            activeSpeakerId === left.id ? ' is-speaking' : ''
          }`}
        >
          <div className="stage__portrait">
            <img src={portraitFor(left.id, left.name)} alt={left.name} loading="eager" />
          </div>
          <div className="stage__figure-name">
            <em>{left.name}</em>
            <span>{left.era}</span>
          </div>
        </aside>

        <div className="stage__center">
          <div className="stage__transcript" ref={transcriptScrollRef}>
            {transcript.length === 0 && !streaming && (
              <p className="stage__empty">
                The arena is yours. Ask the first question to set them off.
              </p>
            )}

            {transcript.map((entry, i) => (
              <Bubble
                key={i}
                side={sideFor(entry.speakerId)}
                name={nameFor(entry.speakerId)}
                content={entry.content}
              />
            ))}

            {streaming && (
              <Bubble
                side={sideFor(streaming.speakerId)}
                name={nameFor(streaming.speakerId)}
                content={visibleStreamingText}
                isStreaming
              />
            )}

            {awaitingContinue && (
              <p className="stage__continue-hint" role="status">
                <kbd>SPACE</kbd>
                <span>to continue</span>
              </p>
            )}

            {error && (
              <div className="stage__error" role="alert">
                {error}
              </div>
            )}
          </div>

          <form className="stage__composer" onSubmit={handleSubmit}>
            <textarea
              ref={inputRef}
              className="stage__input"
              placeholder={
                streaming
                  ? 'Interrupt — ask another question…'
                  : 'Ask a question, steer the conversation…'
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onInputKeyDown}
              rows={1}
              autoFocus
            />
            <button
              type="submit"
              className="stage__send"
              disabled={input.trim().length === 0}
              aria-label="Send"
            >
              <span aria-hidden="true">→</span>
            </button>
          </form>
        </div>

        <aside
          className={`stage__figure stage__figure--right${
            activeSpeakerId === right.id ? ' is-speaking' : ''
          }`}
        >
          <div className="stage__portrait">
            <img src={portraitFor(right.id, right.name)} alt={right.name} loading="eager" />
          </div>
          <div className="stage__figure-name">
            <em>{right.name}</em>
            <span>{right.era}</span>
          </div>
        </aside>
      </section>
    </main>
  );
}

/**
 * Wait for the user to press the spacebar OR for the signal to abort.
 * Ignores keypresses while an input/textarea has focus so the user can
 * still type a new prompt without it counting as "continue".
 *
 * Resolves to `true` when aborted (caller should bail out), `false` when
 * the user pressed space.
 */
function waitForSpace(signal?: AbortSignal): Promise<boolean> {
  return new Promise((resolve) => {
    if (signal?.aborted) return resolve(true);

    const cleanup = () => {
      document.removeEventListener('keydown', onKey);
      signal?.removeEventListener('abort', onAbort);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== 'Space' && e.key !== ' ') return;
      const target = e.target as HTMLElement | null;
      // Don't intercept space while the composer (or any other input) has
      // focus — the user is typing, not asking to advance.
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      cleanup();
      resolve(false);
    };
    const onAbort = () => {
      cleanup();
      resolve(true);
    };
    document.addEventListener('keydown', onKey);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

interface BubbleProps {
  side: 'left' | 'right' | 'user';
  name: string;
  content: string;
  isStreaming?: boolean;
}

function Bubble({ side, name, content, isStreaming }: BubbleProps) {
  return (
    <article
      className={`bubble bubble--${side}${isStreaming ? ' bubble--streaming' : ''}`}
    >
      <header className="bubble__name">{name}</header>
      <div className="bubble__body">
        {content.length === 0 && isStreaming ? (
          <span className="bubble__dots" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
        ) : (
          content
        )}
      </div>
    </article>
  );
}
